class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartClearButton extends HTMLElement {
  constructor() {
    super();

    const clearBtn = this.querySelector('.clear-cart-btn');
    const confirmDiv = this.querySelector('.clear-cart-confirm');
    const confirmBtn = this.querySelector('.confirm-clear');
    const cancelBtn = this.querySelector('.cancel-clear');
    const cartLink = document.querySelector('#cart-icon-bubble');
    const isCartPage = cartLink?.dataset?.isCart === 'true';

    clearBtn.addEventListener('click', () => {
      clearBtn.classList.add('hidden');
      confirmDiv.classList.remove('hidden');
    });

    cancelBtn.addEventListener('click', () => {
      confirmDiv.classList.add('hidden');
      clearBtn.classList.remove('hidden');
    });

    confirmBtn.addEventListener('click', (event) => {
      event.preventDefault();
      const button = event.target.closest('button');

      fetch('/cart/clear.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
        .then(() => {
          if (isCartPage) {
            window.location.reload();
            return;
          }
          return fetch('/?section_id=cart-drawer')
            .then((res) => res.text())
            .then((html) => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, 'text/html');
              const newDrawer = doc.querySelector('#CartDrawer');
              const currentDrawer = document.querySelector('#CartDrawer');

              if (newDrawer && currentDrawer) {
                currentDrawer.replaceWith(newDrawer);
              }

              const currentDrawerContainer = document.querySelector('cart-drawer');
              if (currentDrawerContainer) currentDrawerContainer.classList.add('is-empty');

              const cartCount = document.querySelector('.cart-count-bubble');
              if (cartCount) cartCount.remove();
            });
        })
        .catch((error) => {
          console.error('Error clearing cart:', error);
          this.removeAttribute('disabled');
          button?.classList.remove('loading');
        });
    });
  }
}


customElements.define('cart-clear-button', CartClearButton);

class CartReconfigureButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const button = event.target.closest('button');

      // Store redirection URL and parameters
      const productUrl = button.dataset.url || '';
      const lineItemIndex = this.dataset.index;

      // Create the redirection URL with parameters
      let redirectUrl = productUrl;

      // Display loading state
      this.setAttribute('disabled', true);
      button.classList.add('loading');

      // Directly remove the item using fetch instead of going through CartItems
      // This gives us direct control over the redirect timing
      const body = JSON.stringify({
        line: lineItemIndex,
        quantity: 0,
      });

      fetch(`${routes.cart_change_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: body,
      })
        .then((response) => response.json())
        .then((data) => {
          // Verification that the item is actually removed
          // console.log('Item removed from cart, redirecting to:', redirectUrl);

          // After confirmed cart update, redirect
          window.location.href = redirectUrl;
        })
        .catch((error) => {
          console.error('Error removing item from cart:', error);
          // Remove loading state so user can try again
          this.removeAttribute('disabled');
          button.classList.remove('loading');
        });
    });
  }
}

customElements.define('cart-reconfigure-button', CartReconfigureButton);

/**
 * Removes club configurator lines whose head line (the one carrying _notes) is no longer in the cart.
 * The cart transform can't merge those groups, so they would otherwise show as loose component lines.
 * Only configurator lines (_hidden_item_type) are checked; clubset and headcover bundles are left alone.
 * Shared across cart-items and cart-drawer-items so concurrent renders make a single request.
 */
let headlessBundleCleanup;
function removeHeadlessBundles() {
  if (headlessBundleCleanup) return headlessBundleCleanup;

  headlessBundleCleanup = fetch(`${routes.cart_url}.js`)
    .then((response) => response.json())
    .then((cart) => {
      const groups = {};
      cart.items.forEach((item) => {
        const bundleId = item.properties?._bundleId;
        if (!bundleId || !item.properties._hidden_item_type) return;

        groups[bundleId] = groups[bundleId] || { keys: [], hasHead: false };
        groups[bundleId].keys.push(item.key);
        if (item.properties._notes) groups[bundleId].hasHead = true;
      });

      const updates = {};
      Object.values(groups).forEach((group) => {
        if (group.hasHead) return;
        group.keys.forEach((key) => (updates[key] = 0));
      });
      if (!Object.keys(updates).length) return;

      return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body: JSON.stringify({ updates }) } })
        .then((response) => response.json())
        .then((cartData) => {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'headless-bundle-cleanup', cartData });
        });
    })
    .catch((e) => {
      console.error('Error removing incomplete configurations from cart:', e);
    })
    .finally(() => {
      headlessBundleCleanup = undefined;
    });

  return headlessBundleCleanup;
}

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    this.verifyHero();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });

    removeHeadlessBundles();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  idmeCheck() {
    const cookies = document.cookie.split(';');
    const discountCode = cookies.find((cookie) => cookie.trim().startsWith('discount_code='))?.split('=')[1];
    return discountCode?.includes('-IDME-') || false;
  }

  verifyHero() {
    const idmeCheck = this.idmeCheck();
    const isHero = document.body.dataset.hero === 'true';
    const hasHeroDiscount = document.body.dataset.hasHeroDiscount === 'true';

    if (idmeCheck) document.body.classList.add('hero-verified');

    if ((idmeCheck || isHero) && !hasHeroDiscount) {
      const formData = new FormData();
      formData.append('attributes[HEROES]', 1);

      fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        body: formData,
      })
        .then((response) => response.json())
        .then(() => this.onCartUpdate());

      // Helps prevent loop since cart can reload via AJAX
      document.body.dataset.hero = true;
      document.body.dataset.hasHeroDiscount = true;
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.cart-count-bubble',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-app-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector,
          );
        });
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        // Dispatch custom event for cart updates
        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            bubbles: true,
            detail: { cartData: parsedState, source: 'cart-items', variantId },
          }),
        );

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
